import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, CornerDownLeft, ArrowRight, Command as CommandIcon } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { useOffer } from '@/context/OfferContext';
import { Icon } from '@/components/icons';

export default function CommandPalette() {
  const navigate = useNavigate();
  const { t, tv, lang, setLang } = useLang();
  const { services } = useServices();
  const { openOffer } = useOffer();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const commands = useMemo(() => {
    const nav = [
      { id: 'home', label: lang === 'de' ? 'Startseite' : 'Home', group: lang === 'de' ? 'Navigation' : 'Navigation', icon: 'Cpu', run: () => navigate('/') },
      ...services.map((s) => ({ id: s.slug, label: tv(s.name), sub: tv(s.tagline), group: t('nav.services'), icon: s.icon, accent: s.accent, run: () => navigate(`/leistungen/${s.slug}`) })),
      { id: 'about', label: t('nav.about'), group: 'Navigation', icon: 'Cpu', run: () => navigate('/ueber-uns') },
      { id: 'contact', label: t('nav.contact'), group: 'Navigation', icon: 'Headphones', run: () => navigate('/kontakt') },
      { id: 'portal', label: t('nav.portal'), group: 'Navigation', icon: 'Server', run: () => navigate('/portal') },
    ];
    const actions = [
      { id: 'quote', label: t('cmd.quote'), group: lang === 'de' ? 'Aktionen' : 'Actions', icon: 'CreditCard', run: () => openOffer('') },
      { id: 'nova', label: lang === 'de' ? 'NOVA (KI-Assistent) öffnen' : 'Open NOVA (AI assistant)', group: lang === 'de' ? 'Aktionen' : 'Actions', icon: 'Sparkles', run: () => window.dispatchEvent(new CustomEvent('itw:open-ai')) },
      { id: 'lang', label: lang === 'de' ? 'Sprache: English' : 'Language: Deutsch', group: lang === 'de' ? 'Aktionen' : 'Actions', icon: 'Languages', run: () => setLang(lang === 'de' ? 'en' : 'de') },
    ];
    const fun = [
      { id: 'platform', label: lang === 'de' ? 'Live-Plattform öffnen' : 'Open live platform', group: t('cmd.groupFun'), icon: 'Server', run: () => navigate('/plattform') },
      { id: 'noc', label: t('cmd.noc'), group: t('cmd.groupFun'), icon: 'Globe2', run: () => {
        const doScroll = () => { const el = document.getElementById('noc'); if (el) el.scrollIntoView({ behavior: 'smooth' }); };
        if (window.location.pathname !== '/') { navigate('/'); setTimeout(doScroll, 500); } else doScroll();
      } },
      { id: 'matrix', label: t('cmd.matrix'), group: t('cmd.groupFun'), icon: 'Zap', run: () => window.dispatchEvent(new CustomEvent('itw:toggle-matrix')) },
      { id: 'top', label: t('cmd.top'), group: t('cmd.groupFun'), icon: 'ArrowUp', run: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
    ];
    return [...nav, ...actions, ...fun];
  }, [services, lang, t, tv, navigate, openOffer, setLang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => (c.label + ' ' + (c.sub || '') + ' ' + c.id).toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === 'Escape') setOpen(false);
    };
    const onOpenEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('itw:open-cmdk', onOpenEvt);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('itw:open-cmdk', onOpenEvt); };
  }, []);

  useEffect(() => { if (open) { setQuery(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);
  useEffect(() => { setActive(0); }, [query]);

  const exec = (cmd) => { if (!cmd) return; setOpen(false); setTimeout(() => cmd.run(), 60); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); exec(filtered[active]); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-start justify-center pt-[12vh] px-4"
          data-testid="command-palette"
          onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, y: -16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="relative w-full max-w-xl glass rounded-2xl border border-[color:var(--itw-border-strong)] shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[color:var(--itw-border)]">
              <Search className="h-4 w-4 text-[color:var(--itw-text-3)]" />
              <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
                data-testid="command-palette-input"
                placeholder={lang === 'de' ? 'Suchen oder Befehl eingeben…' : 'Search or type a command…'}
                className="flex-1 bg-transparent outline-none text-[color:var(--itw-text)] text-sm placeholder:text-[color:var(--itw-text-3)]" />
              <kbd className="font-mono text-[10px] text-[color:var(--itw-text-3)] border border-[color:var(--itw-border)] rounded px-1.5 py-0.5">ESC</kbd>
            </div>
            <div className="max-h-[52vh] overflow-y-auto py-2">
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-[color:var(--itw-text-3)]">{lang === 'de' ? 'Keine Treffer' : 'No results'}</div>
              )}
              {filtered.map((c, i) => (
                <button key={c.id} onMouseEnter={() => setActive(i)} onClick={() => exec(c)}
                  data-testid={`cmd-item-${c.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === active ? 'bg-[color:var(--itw-surface-2)]' : ''}`}>
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-[color:var(--itw-surface)] border border-[color:var(--itw-border)] grid place-items-center">
                    <Icon name={c.icon} className="h-4 w-4" style={{ color: c.accent || 'var(--itw-cyan)' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[color:var(--itw-text)] truncate">{c.label}</div>
                    {c.sub && <div className="text-xs text-[color:var(--itw-text-3)] truncate">{c.sub}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-[color:var(--itw-text-3)]">{c.group}</span>
                  {i === active && <ArrowRight className="h-4 w-4 text-[color:var(--itw-cyan)]" />}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-[color:var(--itw-border)] text-[11px] text-[color:var(--itw-text-3)] font-mono">
              <span className="flex items-center gap-1.5"><CornerDownLeft className="h-3 w-3" /> {lang === 'de' ? 'auswählen' : 'select'} · ↑↓ {lang === 'de' ? 'navigieren' : 'navigate'}</span>
              <span className="flex items-center gap-1"><CommandIcon className="h-3 w-3" />K</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
